import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AUTH_TOKEN } from "constants/AuthConstant";
import FirebaseService from "services/FirebaseService";
import AuthService from "services/AuthService";
import { notification } from "antd";

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token: localStorage.getItem(AUTH_TOKEN) || null,
  dataBoAccess: [],
  dataOutletUser: null,
  username: null,
  error: null,
};

export const signIn = createAsyncThunk(
  "auth/login",
  async (data, { rejectWithValue }) => {
    const origination = "back_office";
    const { email, password } = data;
    try {
      const response = await AuthService.login({
        email,
        password,
        origination,
      });
      const token = response.data.token;
      const boAccess = [];
      const boMenus = [];
      const foMenus = [];
      localStorage.setItem(AUTH_TOKEN, token);
      localStorage.setItem("username", response.data.user.name);
      localStorage.setItem("email", response.data.user.email);
      localStorage.setItem("access", response.data.user.access);
      localStorage.setItem("outlet", JSON.stringify(response.data.outlet));
      response.data.menus.bo_menus.forEach((values) => {
        boAccess.push(values.selected);
      });
      response.data.menus.bo_menus.forEach((values) => {
        boMenus.push(values.name);
      });
      response.data.menus.fo_menus.forEach((values) => {
        foMenus.push(values.name);
      });
      localStorage.setItem("boMenus", boMenus.toString());
      localStorage.setItem("foMenus", foMenus.toString());
      localStorage.setItem("boAccess", boAccess.toString());

      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.errors?.message || "Error");
    }
  }
);

export const signUp = createAsyncThunk(
  "auth/register",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;
    try {
      const response = await AuthService.register({ email, password });
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const signOut = createAsyncThunk("auth/logout", async () => {
  const response = await FirebaseService.signOutRequest();
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem("outlet");
  localStorage.removeItem("username");
  localStorage.removeItem("boAccess");
  return response.data;
});

export const signInWithGoogle = createAsyncThunk(
  "auth/signInWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.loginInOAuth();
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const signInWithFacebook = createAsyncThunk(
  "auth/signInWithFacebook",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.loginInOAuth();
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const forgotPass = createAsyncThunk(
  "auth/forgotPass",
  async (data, { rejectWithValue }) => {
    const { email, new_password } = data;
    try {
      const response = await AuthService.forgotPass({
        email,
        new_password,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.errors || "Error");
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.redirect = "/";
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
    getBoAccess: (state) => {
      state.dataBoAccess = localStorage.getItem("boAccess")?.split(",");
    },
    getOutletUser: (state) => {
      state.dataOutletUser = localStorage.getItem("outlet");
    },
    getAuthUsername: (state) => {
      state.username = localStorage.getItem("username");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
        window.location.reload();
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithFacebook.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithFacebook.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithFacebook.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(forgotPass.pending, (state) => {
        state.loading = true;
      })
      .addCase(forgotPass.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/auth/login";
        notification.success({ message: "Proses Berhasil" });
        state.error = action.payload;
      })
      .addCase(forgotPass.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
  getBoAccess,
  getOutletUser,
  getAuthUsername,
} = authSlice.actions;

export default authSlice.reducer;
